import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon, ChatBubbleLeftRightIcon, MagnifyingGlassIcon,
  PaperAirplaneIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import type { SecurePeerMessage } from '../mobile/secureChannel.js';

export interface MessageDirectoryEntry {
  userId: string;
  displayName: string;
  veritasId: string;
  callable: boolean;
}

export function SecureMessaging({ directory }: { directory: MessageDirectoryEntry[] }) {
  const [messages, setMessages] = useState<SecurePeerMessage[]>([]);
  const [peerId, setPeerId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const channel = await import('../mobile/secureChannel.js');
      setMessages(await channel.loadPeerMessages());
      setError(null);
    } catch (cause) {
      setError(messageError(cause));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let active = true;
    const run = () => { if (active && document.visibilityState === 'visible') void refresh(); };
    run();
    const timer = window.setInterval(run, 5_000);
    document.addEventListener('visibilitychange', run);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', run);
    };
  }, [refresh]);

  const names = useMemo(() => new Map(directory.map((entry) => [
    entry.veritasId, entry.displayName,
  ])), [directory]);
  const threads = useMemo(() => {
    const latest = new Map<string, SecurePeerMessage>();
    for (const message of messages) {
      const prior = latest.get(message.peerVeritasId);
      if (!prior || Date.parse(message.sentAt) > Date.parse(prior.sentAt)) {
        latest.set(message.peerVeritasId, message);
      }
    }
    return [...latest.values()].sort((left, right) => Date.parse(right.sentAt) - Date.parse(left.sentAt));
  }, [messages]);
  const selectedMessages = messages.filter((message) => message.peerVeritasId === peerId);
  const selectedDirectory = directory.find((entry) => entry.veritasId === peerId);

  useEffect(() => {
    if (peerId) endRef.current?.scrollIntoView({ block: 'end' });
  }, [peerId, selectedMessages.length]);

  async function send() {
    if (!peerId || !draft.trim() || busy || error) return;
    setBusy(true);
    try {
      const channel = await import('../mobile/secureChannel.js');
      const sent = await channel.sendPeerMessage(peerId, draft);
      setMessages((current) => [...current, sent]);
      setDraft('');
      setError(null);
      setNotice(null);
    } catch (cause) {
      if (messageErrorCode(cause) === 'peer_prekeys_unavailable') {
        setNotice(messageError(cause));
      } else {
        setError(messageError(cause));
        setNotice(null);
      }
    } finally { setBusy(false); }
  }

  if (peerId) {
    const safetyNumber = selectedMessages.at(-1)?.safetyNumber;
    return <section aria-labelledby="secure-thread-title" className="min-h-[calc(100vh-190px)]">
      <header className="sticky top-[64px] z-10 -mx-4 border-b border-neutral-300 bg-bg px-4 pb-3">
        <button type="button" className="btn btn-ghost px-2" onClick={() => {
          setPeerId(null);
          setNotice(null);
        }}>
          <ArrowLeftIcon className="h-5 w-5" aria-hidden /> Messages
        </button>
        <div className="mt-2 flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-accent-100 font-semibold text-accent-800"
            aria-hidden>{initials(names.get(peerId) ?? 'VERITAS')}</span>
          <span className="min-w-0 flex-1">
            <h2 id="secure-thread-title" className="truncate text-h4">{names.get(peerId) ?? 'VERITAS contact'}</h2>
            <span className="block truncate font-mono text-[10px] text-neutral-700">{peerId}</span>
          </span>
        </div>
      </header>
      <SecurityBanner failed={Boolean(error)} />
      {safetyNumber && <details className="mt-3 border border-neutral-300 bg-neutral-100 px-3 py-2 text-xs">
        <summary className="cursor-pointer font-semibold">Verify device safety number</summary>
        <p className="mt-2 font-mono">{safetyNumber}</p>
        <p className="mt-1 text-neutral-700">Compare this number through a separate trusted channel.</p>
      </details>}
      <div className="space-y-2 py-4" aria-live="polite">
        {selectedMessages.length === 0 && <p className="py-10 text-center text-sm text-neutral-700">
          Start a protected VERITAS conversation.
        </p>}
        {selectedMessages.map((message) => <article key={`${message.direction}:${message.messageId}`}
          className={`max-w-[86%] rounded-2xl px-3 py-2 ${message.direction === 'sent'
            ? 'ml-auto bg-accent-700 text-white' : 'mr-auto bg-neutral-200 text-text'}`}>
          <p className="whitespace-pre-wrap break-words text-[15px]">{message.text}</p>
          <p className={`mt-1 text-right text-[10px] ${message.direction === 'sent'
            ? 'text-accent-100' : 'text-neutral-700'}`}>
            {formatMessageTime(message.sentAt)}{message.direction === 'sent' ? ` · ${message.state}` : ''}
          </p>
        </article>)}
        <div ref={endRef} />
      </div>
      {error && <p className="form-error mb-3" role="alert">{error}</p>}
      {notice && <p className="mb-3 border border-neutral-300 bg-neutral-100 p-3 text-sm"
        role="status">{notice}</p>}
      <form className="sticky bottom-[64px] -mx-4 flex gap-2 border-t border-neutral-300 bg-bg p-3"
        onSubmit={(event) => { event.preventDefault(); void send(); }}>
        <label className="sr-only" htmlFor="secure-message-text">Message</label>
        <textarea id="secure-message-text" className="input min-h-touch flex-1 resize-none py-2"
          rows={1} maxLength={4000} value={draft} disabled={busy || Boolean(error)}
          placeholder="Message" onChange={(event) => setDraft(event.target.value)} />
        <button type="submit" className="btn btn-primary h-touch w-touch px-0" aria-label="Send protected message"
          disabled={busy || Boolean(error) || !draft.trim()}>
          <PaperAirplaneIcon className="h-5 w-5" aria-hidden />
        </button>
      </form>
    </section>;
  }

  const normalized = query.normalize('NFKC').trim().toLocaleLowerCase();
  const candidates = directory.filter((entry) => !normalized
    || `${entry.displayName} ${entry.veritasId}`.toLocaleLowerCase().includes(normalized));
  return <section aria-labelledby="secure-messages-title">
    <div className="flex items-center gap-3">
      <ChatBubbleLeftRightIcon className="h-7 w-7 text-accent-700" aria-hidden />
      <div>
        <p className="text-h6 uppercase tracking-[.08em] text-accent-700">VERITAS peer messaging</p>
        <h2 id="secure-messages-title" className="text-h3">Messages</h2>
      </div>
    </div>
    <SecurityBanner failed={Boolean(error)} />
    {error && <p className="form-error mt-3" role="alert">{error}</p>}
    <label className="field relative mt-4" htmlFor="message-contact-search">
      <span className="sr-only">Search conversations and contacts</span>
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-neutral-600" aria-hidden />
      <input id="message-contact-search" className="input pl-10" type="search" value={query}
        placeholder="Search conversations and contacts" autoComplete="off"
        onChange={(event) => setQuery(event.target.value)} />
    </label>
    {!normalized && threads.length > 0 && <section className="mt-5" aria-labelledby="recent-message-title">
      <h3 id="recent-message-title" className="text-h4">Recent</h3>
      <div className="mt-2 divide-y divide-neutral-300 border-y border-neutral-300">
        {threads.map((thread) => <MessageContactRow key={thread.peerVeritasId}
          name={names.get(thread.peerVeritasId) ?? 'VERITAS contact'}
          detail={thread.text} onClick={() => setPeerId(thread.peerVeritasId)} />)}
      </div>
    </section>}
    <section className="mt-5" aria-labelledby="message-directory-title">
      <h3 id="message-directory-title" className="text-h4">Directory</h3>
      {loading ? <p className="py-8 text-center text-sm text-neutral-700">Preparing protected messaging…</p>
        : candidates.length === 0 ? <p className="py-8 text-center text-sm text-neutral-700">No matching contacts.</p>
          : <div className="mt-2 divide-y divide-neutral-300 border-y border-neutral-300">
            {candidates.map((entry) => <MessageContactRow key={entry.userId}
              name={entry.displayName} detail={entry.callable ? 'VERITAS identity' : 'Secure device not ready'}
              disabled={!entry.callable || Boolean(error)} onClick={() => setPeerId(entry.veritasId)} />)}
          </div>}
    </section>
  </section>;
}

function SecurityBanner({ failed }: { failed: boolean }) {
  return <div className={`mt-4 flex items-center gap-3 border p-3 ${failed
    ? 'border-alert-400 bg-alert-100' : 'border-presence-active bg-neutral-100'}`}
    role="status" aria-live="polite">
    <ShieldCheckIcon className={`h-6 w-6 shrink-0 ${failed ? 'text-alert-700' : 'text-presence-active'}`} aria-hidden />
    <span>
      <strong className="block">{failed
        ? 'PQmTLS or PQE2EE protection interrupted'
        : 'PQmTLS + PQE2EE active'}</strong>
      <span className="block text-xs text-neutral-700">{failed
        ? 'Sending is disabled. Reconnect the protected channel before communicating.'
        : 'AES-256-GCM message content stays inside the recipient-device envelope.'}</span>
    </span>
  </div>;
}

function MessageContactRow({ name, detail, disabled = false, onClick }: {
  name: string; detail: string; disabled?: boolean; onClick: () => void;
}) {
  return <button type="button" disabled={disabled} onClick={onClick}
    className="flex min-h-[68px] w-full items-center gap-3 px-2 py-2 text-left disabled:opacity-50">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent-100 font-semibold text-accent-800"
      aria-hidden>{initials(name)}</span>
    <span className="min-w-0 flex-1">
      <strong className="block truncate">{name}</strong>
      <span className="block truncate text-sm text-neutral-700">{detail}</span>
    </span>
    <ChatBubbleLeftRightIcon className="h-5 w-5 text-accent-700" aria-hidden />
  </button>;
}

function initials(value: string): string {
  return value.trim().split(/\s+/u).slice(0, 2).map((part) => part.charAt(0)).join('').toLocaleUpperCase() || 'V';
}

function formatMessageTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(undefined, {
    hour: 'numeric', minute: '2-digit',
  }).format(date);
}

function messageError(cause: unknown): string {
  const code = messageErrorCode(cause);
  if (code === 'peer_prekeys_unavailable') {
    return 'That person must open the updated VERITAS app once before protected messages can be delivered.';
  }
  if (code === 'peer_identity_key_changed') {
    return 'A contact device key changed. Protected messaging stopped. Verify the device with an administrator.';
  }
  if (code === 'peer_message_prekey_missing') {
    return 'A single-use device key was unavailable. Protected messaging stopped before opening the message.';
  }
  if (code === 'mobile_activation_required' || code === 'mobile_session_required') {
    return 'Secure activation expired. Sign in again to restore protected messaging.';
  }
  if (/^mobile_gateway_http_(?:502|503|504)$/u.test(code)) {
    return 'The protected service is restarting or temporarily unavailable. Try again in a moment.';
  }
  if (code === 'mobile_gateway_response_invalid') {
    return 'The protected service returned an invalid response. No message was sent.';
  }
  return 'Protected messaging could not complete this request. Check the network and try again.';
}

function messageErrorCode(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
